import { c as _c } from "react/compiler-runtime";
import { AuthRole } from '@newfront-insurance/auth-api';
import { LogoSpinner } from '@newfront-insurance/core-ui';
import { hasAllScopes, hasAnyScope } from '@newfront-insurance/next-auth';
import type { Route } from '@newfront-insurance/next-router-provider';
import { useRouter } from '@newfront-insurance/next-router-provider';
import { useEffect, type JSX } from 'react';
import * as React from 'react';
import invariant from 'tiny-invariant';
import { useLoggedInUser } from '@/client/hooks/use-logged-in-user';
enum MATCH {
  EXACT = 'EXACT',
  // The user ONLY has these exact roles (and no more)
  ANY = 'ANY',
  // The user has ANY of the roles
  ALL = 'ALL' // The user has ALL of these roles (but maybe more)
  ,
}
interface Redirect {
  roles: AuthRole[];
  pathname: string;
  match: MATCH;
}
const OVERRIDES: Redirect[] = [{
  roles: [AuthRole.GROWTH_CALLER],
  pathname: '/accounts/create',
  match: MATCH.ALL
},
// The redirect with ensure that any user with this role AND any other role will be redirected to the accounts app.
// There are cases where producers are also account managers, the AM role beats the producer role and so they should
// be sent to accounts. When a user is an admin and has many roles, they are also forced to visit accounts.
{
  roles: [AuthRole.PRODUCER, AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER, AuthRole.ACCOUNT_MANAGER, AuthRole.AM_MANAGER, AuthRole.ENGINEER],
  pathname: '/accounts',
  match: MATCH.ANY
}];

/**
 * This provider should be placed within a LoginBoundary. When the user exists and the current
 * path is `/` it will automatically redirect the user to the app based on their role.
 * @returns
 */
export function PostLoginRedirectProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const user = useLoggedInUser();
  const {
    pushRoute,
    router
  } = useRouter();
  const isRoot = router.pathname === "/";
  invariant(user, "PostLoginRedirect should be within a LoginBoundary so the user is guaranteed to exist");
  let t1;
  let t2;
  if ($[0] !== isRoot || $[1] !== pushRoute || $[2] !== user) {
    t1 = () => {
      if (isRoot) {
        pushRoute(getAppRoute(user.roles));
      }
    };
    t2 = [isRoot, pushRoute, user];
    $[0] = isRoot;
    $[1] = pushRoute;
    $[2] = user;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (isRoot) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <LogoSpinner />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  return children as JSX.Element;
}

/**
 * Take the user roles and return a route to redirect the user to the correct path.
 * @param roles User roles from the JWT token
 * @returns Route that can be used by pushRoute
 */
function getAppRoute(roles: string[]): Route {
  // Default redirect
  let pathname = '/accounts';
  OVERRIDES.forEach(redirect => {
    if (redirect.match === MATCH.ALL && hasAllScopes(roles, redirect.roles)) {
      // Check if user has all roles
      pathname = redirect.pathname;
    } else if (redirect.match === MATCH.ANY && hasAnyScope(roles, redirect.roles)) {
      // User has any of the roles
      pathname = redirect.pathname;
    }
  });
  return {
    pathname
  };
}